import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useAuth } from '../contexts/AuthContext';

/**
 * Persistent yellow banner shown on every screen while an impersonation session
 * is active. Tap the "Stop" button to end the session and restore the super-user's
 * original login.
 *
 * Usage: render at the top of each screen (or once inside the app's Root Navigator)
 * below the status bar. It self-hides when !isImpersonating.
 */
const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, user, impersonatorLogin, stopImpersonation } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!isImpersonating) return null;

  const handleStop = () => {
    Alert.alert(
      'Stop impersonation?',
      `You will return to your own account (${impersonatorLogin}).`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Stop',
          style: 'destructive',
          onPress: async () => {
            setBusy(true);
            try {
              await stopImpersonation();
            } catch (e) {
              Alert.alert('Error', 'Failed to stop impersonation. You have been logged out.');
            } finally {
              setBusy(false);
            }
          },
        },
      ],
    );
  };

  return (
    <View style={styles.banner}>
      <Text style={styles.text} numberOfLines={1}>
        Impersonating: {user?.login ?? '—'}
      </Text>
      <TouchableOpacity onPress={handleStop} disabled={busy} style={styles.btn}>
        {busy ? (
          <ActivityIndicator size="small" color="#111" />
        ) : (
          <Text style={styles.btnText}>Stop</Text>
        )}
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#FFC107',
  },
  text: {
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: '600',
    color: '#111',
    flex: 1,
  },
  btn: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: '#111',
  },
  btnText: {
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: '600',
    color: '#FFC107',
  },
});

export default ImpersonationBanner;
